import { ReportResponseTypes } from '@mode/shared/data-access-webapp';
import { SpaceType } from '@mode/shared/contract-common';
import { DataViewResponseTypes } from '@mode/shared/contract-common';
import { ReportViewerFeatureState } from './report-viewer/report-viewer.reducer';

/**
 * Create reportViewer feature from the BE response.
 *
 * For the time being, reportView can be of any type in this data mapper, however, the differences between the types
 * are not impactful to this data mapper. In the near term, much of this will be refactored to fit HAL api patterns.
 */
export function reportAndViewResponseToFeatureState(
  report: ReportResponseTypes.ReportData,
  reportView:
    | ReportResponseTypes.ReportViewerLastSuccessfulRunResponse
    | ReportResponseTypes.ReportViewerLastRunResponse
    | ReportResponseTypes.ReportViewerRunInContextResponse
): ReportViewerFeatureState {
  return {
    accountUsername: report.account_username,
    colorMappings: report.color_mappings,
    createdAt: report.created_at,
    creator: report.creator,
    description: report.description,
    displayName: report.display_name,
    drilldownsEnabled: report.drilldowns_enabled,
    drillAnywhereEnabled: report.drill_anywhere_enabled,
    editedAt: report.edited_at,
    expectedRuntime: reportView.expected_runtime,
    filters: report._embedded.filters ? report._embedded.filters._embedded.report_filters : null,
    fullWidth: report.full_width,
    hasNotebook: report.has_notebook,
    hasSuccessfulReportRun: report.has_successful_report_run,
    iframeSrc: report.iframe_src,
    isLatestReportRun: reportView._embedded.report_run.is_latest_report_run,
    isLatestSuccessfulReportRun: reportView._embedded.report_run.is_latest_successful_report_run,
    lastRunAt: report.last_run_at,
    lastSavedAt: report.last_saved_at,
    lastSuccessfulSyncAt: report.last_successful_sync_at,
    lastSuccessfullyRunAt: report.last_successfully_run_at,
    layout: report.layout,
    manualRunDisabled: report.manual_run_disabled,
    mostRecentReportRunToken: reportView.most_recent_report_run_token,
    mostRecentReportRunState: reportView.most_recent_report_run_state,
    name: report.name,
    parentReportName: report.parent_report_name,
    parentReportLink: report.parent_report_link,
    publishedAt: report.published_at,
    reportHasFailuresSinceLastSuccess: reportView._embedded.report_run.report_has_failures_since_last_success,
    reportRunFormFields: reportView._embedded.report_run.form_fields,
    reportRunState: reportView._embedded.report_run.state,
    reportRunToken: reportView._embedded.report_run.token,
    reportRunCompletedAt: reportView._embedded.report_run.completed_at,
    runPrivately: report.run_privately,
    showFilterHeader: report.show_filter_header,
    showReportHistory: report.show_report_history,
    spaceName: report._embedded.space?.name,
    spaceDefaultAccess: report._embedded.space?.default_access_level,
    spaceUrl: report._embedded.space?._links.web?.href,
    isSpaceViewable: report._embedded.space?.['viewable?'] ?? false,
    isCollectionPersonal: report._embedded.space?.space_type === SpaceType.private,
    isCollectionCommunity: report._embedded.space?.space_type === SpaceType.community,
    theme: report._embedded.report_theme
      ? {
          name: report._embedded.report_theme.name,
          token: report._embedded.report_theme.token,
          href: report._embedded.report_theme.css_href,
          customSource: report._embedded.report_theme.css_source,
        }
      : {
          // fallback theme
          name: 'Fallback',
          token: 'fallback_theme',
          href: (window as any).ASSET_PATH_MAP.embedReportCSS, // can't reference asset service from Angular
          customSource: undefined,
        },
    token: report.token,
    updatedAt: report.updated_at,
    webLink: report.web_link,
  };
}

/**
 * Helper function to validate whether a dataset embed is present on a SwitchViewResource.
 *
 * TODO: Note that this is only temporary to accommodate for the viewer flow which is not yet up to the current
 *   data-access pattern standards. Once the `/internal` apis are removed and the viewer is refactored to use
 *   currently expected patterns, this duplicate will be removed for the version in switch-view-data-mappers.
 *   At this point, we can't/shouldn't import from there into a domain grouping, so this is temporary duplicated.
 */
export function switchViewResponseHasDataset(switchViewResponse: DataViewResponseTypes.SwitchViewResponses): boolean {
  const embeds = switchViewResponse._embedded;
  if (
    embeds &&
    ('last_data_run' in embeds || 'last_successful_data_run' in embeds || 'data_run_for_run_in_context' in embeds)
  ) {
    return true;
  }

  return false;
}
